import type { ExchangeRates } from '../est/currency/currency.module';
import type { EstGlobalDebounce } from '../estoffice/common/common.module';
import type { UnitStatus } from '../services/api';
import type { RootScope } from '../types';
import type { UnitSearchPanelFilterService } from './unit.search.panel.filter.service';
import type { UnitSearchPanel as UnitSearchPanelType } from './unit.search.panel.resource';

import { opportunityApi } from '../services/api/opportunitiesApi';
import { isPublicMode } from '../services/publicMode';
import { getSharedWorkspaceContext } from '../services/sharedWorkspace';
import { type FilterSpec, QueryBuilder } from './queryBuilder';

unitSearchPanelCtrl.$inject = [
  'UnitSearchPanel',
  '$scope',
  '$rootScope',
  '$location',
  '$window',
  'unit.search.panel.filter',
  'debounce',
  'exchangeRates',
  'localStorageService',
];

interface ScopeProps {
  houseId: string;
  sectionId: string;

  /**
   * The same as company.currency
   * TODO: migrate to RootScope.company in future?
   */
  defaultCurrency: string | 'false';

  verticalLayout?: 'true' | 'false';
}

interface DropdownStatus extends UnitStatus {
  selected: boolean;
}

export interface unitSearchPanelCtrl extends ScopeProps {
  publicMode: boolean;
  company: RootScope['company'];
  exchangeRates: ExchangeRates;
  request: QueryBuilder;

  /**
   * Status list for filter dropdown
   */
  availableStatuses: DropdownStatus[];
  initStatusList(statuses: UnitStatus[]): void;
  onStatusSelect(data: DropdownStatus): void;

  setNaviMode(mode: RootScope['naviMode']): void;
  toggleStackView(): void;
  togglePriceView(): void;
  toggleShowSold(): void;
  resetQuery(): void;
  updateExchangeRate(selectedCurrency: string): void;
}

export function unitSearchPanelCtrl(
  this: unitSearchPanelCtrl,
  UnitSearchPanel: UnitSearchPanelType,
  $scope: angular.IScope,
  $rootScope: RootScope,
  $location: angular.ILocationService,
  $window: angular.IWindowService,
  filterService: UnitSearchPanelFilterService,
  debounce: EstGlobalDebounce,
  exchangeRates: ExchangeRates,
  localStorageService: angular.local.storage.ILocalStorageService,
) {
  const vm = this;
  const sharedCtx = getSharedWorkspaceContext();
  vm.publicMode = isPublicMode();
  vm.company = $rootScope.company;
  vm.exchangeRates = exchangeRates;
  vm.availableStatuses = [];

  $rootScope.$on('unit.search.panel:resetFilters', () => vm.resetQuery());

  // filterChangeReady: skip the very first watch fire (initial Angular render)
  // suppressFilterChangeLog: set before programmatic filter changes to avoid false log entries
  let filterChangeReady = false;
  let suppressFilterChangeLog = false;

  $rootScope.$on('sw.filter.apply', (_evt, data: { filters: FilterSpec[] }) => {
    if (data?.filters) {
      suppressFilterChangeLog = true;
      vm.request.applyFromFilterSpecs(data.filters);
      if (vm.availableStatuses.length > 0) {
        vm.initStatusList(vm.availableStatuses);
      }
      $scope.$applyAsync();
    }
  });

  // Exposed for vanilla JS (shared workspace header filter buttons)
  (window as any).swGetCurrentFilters = () => vm.request.toFilters();

  $rootScope.updateCurrencyRates();

  vm.request = QueryBuilder.load({
    defaultCurrency: vm.defaultCurrency,
    exchangeRates: vm.exchangeRates,
    localStorageService,
  });

  let dbFiltersLoaded = false;

  if (!QueryBuilder.hasSavedState(localStorageService)) {
    let fetchPromise: Promise<unknown[] | null> | null = null;

    if (sharedCtx?.opportunityId && sharedCtx?.urlToken) {
      fetchPromise = opportunityApi.getSelectedSegmentFiltersForVisitor({
        opportunityId: sharedCtx.opportunityId,
        urlToken: sharedCtx.urlToken,
      });
    } else {
      const lead: { id?: string } | null = localStorageService.get('lead');

      if (lead?.id) {
        fetchPromise = opportunityApi.getSelectedSegmentFilters(lead.id);
      }
    }

    if (fetchPromise) {
      fetchPromise
        .then((filters) => {
          if (filters && filters.length > 0) {
            suppressFilterChangeLog = true;
            dbFiltersLoaded = true;
            vm.request.applyFromFilterSpecs(filters as FilterSpec[]);

            if (vm.availableStatuses.length > 0) {
              vm.initStatusList(vm.availableStatuses);
            }

            $scope.$applyAsync();
          }
        })
        .catch(() => {});
    }
  }

  $scope.$watch(
    'vm.request',
    function () {
      const filters: FilterSpec[] = vm.request.toFilters();

      // Skip the very first watch fire only if there are no active filters yet.
      // If filters were already loaded from localStorage (QueryBuilder.load),
      // they are present on the first fire and must be applied immediately.
      if (!filterChangeReady) {
        filterChangeReady = true;

        if (!filters.some((item) => item.name !== 'status_id')) {
          return;
        }
      }

      // we need at least one filter active (except status)
      // in order to start performing filtration
      if (filters.some((item) => item.name !== 'status_id')) {
        debounce(() => {
          filterService.requestFilter({
            sectionId: vm.sectionId,
            publicMode: vm.publicMode,
            byHouseMode:
              $location.absUrl().includes('/house') ||
              $rootScope.naviMode === 'byHouse',
            filters,
          });

          vm.request.save();

          if (
            !suppressFilterChangeLog &&
            sharedCtx?.opportunityId &&
            sharedCtx?.urlToken
          ) {
            opportunityApi
              .logSelectedSegmentAction({
                opportunityId: sharedCtx.opportunityId,
                urlToken: sharedCtx.urlToken,
                action: opportunityApi.ACTION.FILTERS_CHANGE,
              })
              .catch(() => {});
          }

          suppressFilterChangeLog = false;
        }, 400);
      } else {
        filterService.reset();
        suppressFilterChangeLog = false;
      }
    },
    true,
  );

  UnitSearchPanel.getStatuses((statuses) => {
    statuses.sort((a, b) => Number(a.type) - Number(b.type));

    vm.initStatusList(statuses);
  });

  vm.onStatusSelect = (data) => vm.request.statuses.toggleSelection(data.id);

  vm.setNaviMode = (mode) => {
    $rootScope.naviMode = mode;
    localStorageService.set('stackingPlanNaviMode', mode);

    const swBase = (window as any).sharedWorkspaceBaseUrl as string | undefined;

    if (swBase) {
      // Shared workspace uses section-based URLs; sectionId works for both modes
      $window.location.href = `${swBase}/${vm.sectionId}`;
    } else if (mode === 'byHouse') {
      $window.location.href = `/house/${vm.houseId}`;
    } else {
      $window.location.href = `/section/${vm.sectionId}`;
    }
  };

  $scope.$watch('$root.company', function () {
    if ($rootScope.company) {
      vm.company = $rootScope.company;
    }
  });

  vm.toggleStackView = () => {
    $rootScope.stackIsMinimized = !$rootScope.stackIsMinimized;

    if ($rootScope.stackIsMinimized) {
      localStorageService.set('stackIsMinimized', true);
    } else {
      localStorageService.remove('stackIsMinimized');
    }
  };

  vm.togglePriceView = () => {
    $rootScope.showPriceInView = !$rootScope.showPriceInView;

    if ($rootScope.showPriceInView) {
      if ($rootScope.stackIsMinimized) {
        vm.toggleStackView();
      }

      localStorageService.set('showPriceInView', true);
    } else {
      localStorageService.remove('showPriceInView');
    }
  };

  vm.toggleShowSold = () => {
    $rootScope.showSold = !$rootScope.showSold;

    if ($rootScope.showSold) {
      localStorageService.remove('hideSold');
    } else {
      localStorageService.set('hideSold', true);
    }
  };

  vm.resetQuery = () => {
    vm.request.reset();

    vm.initStatusList(vm.availableStatuses);
  };

  vm.initStatusList = (statuses) => {
    const hasSavedState = QueryBuilder.hasSavedState(localStorageService);

    vm.availableStatuses = statuses.map((status) => {
      // apply defaults only when no local state and no DB filters loaded
      if (!hasSavedState && !dbFiltersLoaded) {
        switch (status.type) {
          case '2': // free
          case '3': // reserved
            vm.request.statuses.addSelection(status.id);
            break;
          case '1': // closed
          case '4': // sold
          default:
            break;
        }
      }

      return {
        ...status,
        selected: vm.request.statuses.containsSelection(status.id),
      };
    });
  };

  vm.updateExchangeRate = (selectedCurrency) => {
    vm.request.price.setCurrency(selectedCurrency);
  };
}
